import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { faBroom } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { SeoProfessionals } from '@medprev-web-app/api-graphql-client';

import { Card } from '@/components/ui/card.component';
import { Link } from '@/components/ui/link/link.component';
import { Button } from '@/components/ui/button/button.component';
import { formatToTitleCase } from '@/utils/formatters/format-to-title-case';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface ProfessionalsCardByLetterProps {
  letter: string;
  professionals?: SeoProfessionals[];
  hasFilter?: boolean;
  shouldShowLetter?: boolean;
}

export function ProfessionalsCardByLetter({
  letter,
  hasFilter,
  professionals = [],
  shouldShowLetter = true,
}: ProfessionalsCardByLetterProps) {
  const hasProfessionals = professionals.length > 0;
  const lowercaseLetter = letter.toLocaleLowerCase();

  const resolveProfessionalName = (professional: SeoProfessionals): string => {
    const { name, honorific } = professional;

    if (!honorific) {
      return formatToTitleCase(name!);
    }

    return formatToTitleCase(`${honorific} ${name}`);
  };

  return (
    <Card className="md:mt-6 pt-0 border-x-0 border-b-0 md:border-b md:border-x rounded-none md:rounded-xl sm:px-16 md:px-6">
      <div>
        {shouldShowLetter && (
          <Link
            prefetch={false}
            href={`/profissionais/por-letra/${lowercaseLetter}/`}
            className="group w-fit flex"
            data-analytics-event={AnalyticsEnum.CLICK_PROFESSIONAL_FOCUS_LETTER}
            data-analytics-properties={JSON.stringify({ letter: lowercaseLetter })}
          >
            <div className="py-1 px-5 bg-green-100 rounded-b w-fit group-hover:bg-green-200">
              <h2 className="font-semibold text-gray-700 capitalize group-hover:underline">
                {letter}
              </h2>
            </div>
          </Link>
        )}

        {hasProfessionals
          ? (
              <ul className="mt-4 space-y-4">
                {professionals.map((professional, index) => {
                  const formattedName = resolveProfessionalName(professional);

                  return (
                    <li key={professional.slug} className="space-y-4 ">
                      <Link
                        prefetch={false}
                        data-testid={
                          DataTestIdEnum.PROFESSIONALS_INDEX_LINK_BY_PROFESSIONAL_NAME
                        }
                        href={`/profissionais/${professional.slug}/`}
                        className="text-green-500 font-bold hover:underline"
                      >
                        {formattedName}
                      </Link>

                      {hasFilter && index < professionals.length - 1 && <hr />}
                      {!hasFilter && <hr />}
                    </li>
                  );
                })}
              </ul>
            )
          : (
              <div className="mt-4 space-y-4">
                <p className="text-gray-500">
                  Não encontramos um profissional com essa letra. Experimente limpar
                  o filtro e encontrar outros profissionais disponíveis.
                </p>

                <Button startIcon={faBroom} asChild>
                  <Link prefetch={false} href="/profissionais/" data-analytics-event={AnalyticsEnum.CLICK_PROFESSIONAL_INDEX_CLEAR_FILTER}>Limpar filtro</Link>
                </Button>
              </div>
            )}

        {!hasFilter && (
          <Button
            variant="text"
            className="w-fit mt-4"
            startIcon={faPlus}
            asChild
          >
            <Link
              prefetch={false}
              data-analytics-event={AnalyticsEnum.CLICK_PROFESSIONAL_INDEX_VIEW_MORE}
              data-testid={DataTestIdEnum.PROFESSIONALS_INDEX_SEE_MORE_BY_LETTER}
              href={`/profissionais/por-letra/${lowercaseLetter}/`}
            >
              Ver mais
            </Link>
          </Button>
        )}
      </div>
    </Card>
  );
}
